import { formatMmAsCm } from '~/utils/units';
import { Measurement } from '~/domain/measurement/measurement';
import {
    IntensityRegion,
    rectangleFrame,
    RegionGeometry,
} from '~/domain/intensity/intensityRegion';
import {
    formatImageIntensity,
    imageIntensityUnit,
} from '~/domain/dicom/intensityUnits';
import { Image3D } from '~/domain/patient/image3d';

/**
 * Display formatting for measurements. This is the application/UI boundary
 * where the domain unit (mm) is converted to the display unit (cm), per
 * docs/ui_specs.md. Distances are macro distances → 2 decimal places in
 * cm; angles are reported in degrees → 1 decimal place. The unit label is
 * always included.
 */

/** Decimal places for distance display (macro distance, cm). */
const DISTANCE_DECIMALS = 2;
/** Decimal places for angle display (degrees). */
const ANGLE_DECIMALS = 1;

/** Format a mm distance as a cm display string, e.g. `"2.54 cm"`. */
export function formatDistanceCm(distanceMm: number): string {
    return `${formatMmAsCm(distanceMm, DISTANCE_DECIMALS)} cm`;
}

/**
 * Format a region's size: a circle's diameter, e.g. `"⌀ 3.20 cm"`, or a
 * rectangle's sides, first along its first side, e.g. `"3.20 × 1.50 cm"`.
 */
export function formatRegionSize(region: RegionGeometry): string {
    if (region.shape === 'circle') {
        return `⌀ ${formatDistanceCm(2 * region.radiusMm)}`;
    }
    const { widthMm, heightMm } = rectangleFrame(region.cornersMm);
    const width = formatMmAsCm(widthMm, DISTANCE_DECIMALS);
    return `${width} × ${formatDistanceCm(heightMm)}`;
}

/**
 * A region's on-image label, e.g. `"⌀ 3.20 cm · 45.0 ± 12.3 HU"`: its size,
 * then the mean ± SD inside it in `image`'s unit, which fits the SD too as
 * the display conversion is a plain scale. Without the image, its figures or
 * a unit (MR), the intensity is left out: a bare number would be read in no
 * unit.
 */
export function formatRegionLabel(
    region: RegionGeometry & Pick<IntensityRegion, 'stats'>,
    image: Image3D | undefined
): string {
    const size = formatRegionSize(region);
    const unit = imageIntensityUnit(image);
    const { stats } = region;
    if (!image || !stats || !unit) {
        return size;
    }
    const [mean, sd] = [stats.mean, stats.std].map((value) =>
        formatImageIntensity(value, image, '')
    );
    return `${size} · ${mean} ± ${sd} ${unit}`;
}

/** Format an angle in degrees as a display string, e.g. `"42.5°"`. */
export function formatAngleDeg(angleDeg: number): string {
    return `${angleDeg.toFixed(ANGLE_DECIMALS)}°`;
}

/** Format a committed measurement as its primary display value. */
export function formatMeasurementValue(measurement: Measurement): string {
    return measurement.kind === 'distance'
        ? formatDistanceCm(measurement.distanceMm)
        : formatAngleDeg(measurement.angleDeg);
}
